/**
 * @fileoverview AI Test Data Generation — Multi-Agent Chat Interface
 * @description Clean chat UI with agent orchestration sidebar showing real-time
 *              agent delegation, execution status, and results.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Fade,
  LinearProgress,
  Avatar,
} from '@mui/material';
import {
  Send,
  Bot,
  Brain,
  Clock,
  Ticket,
  Plane,
  Package,
  CheckCircle2,
  Loader2,
  Sparkles,
  Zap,
  ChevronRight,
  Anchor,
  Users,
  Shield,
  MapPin,
  CalendarClock,
  Luggage,
  CreditCard,
  Truck,
  BarChart3,
  List,
} from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { aiTestDataApi } from '../services/aiTestDataApi';
import TdmBookings from './TdmBookings';
import './AITestDataGeneration.css';

/* Visual pacing delays (ms) — disabled in tests */
const PACING_DELAY = import.meta.env.MODE === 'test' ? 0 : 3000;
const DELEGATION_DELAY = import.meta.env.MODE === 'test' ? 0 : 1200;

/** Simple non-crypto jitter for visual pacing (0–999ms). Not used for security. */
let _pacingCounter = 0;
const getVisualJitter = () => (++_pacingCounter * 397) % 1000;

/* ─── Agent Config with Groups ─── */
const AGENT_CONFIG = [
  { id: 'orchestrator', name: 'Orchestrator', description: 'Coordinates tasks between specialized agents', icon: Brain, color: '#6366F1', online: true },
  { id: 'flight_scheduling', name: 'Flight Scheduling', description: 'Retrieves flight details and applies delays', icon: Clock, color: '#0097A9', group: 'FlightOps', online: true },
  { id: 'flight_creation', name: 'Flight Creation', description: 'Creates new flight entries', icon: Plane, color: '#0097A9', group: 'FlightOps', online: true },
  { id: 'gate_management', name: 'Gate Management', description: 'Assigns and manages gates', icon: MapPin, color: '#0097A9', group: 'FlightOps', online: true },
  { id: 'schedule_optimizer', name: 'Schedule Optimizer', description: 'Optimizes schedules for efficiency', icon: CalendarClock, color: '#0097A9', group: 'FlightOps', online: false },
  { id: 'crew_assignment', name: 'Crew Assignment', description: 'Finds crew assigned to flights (Crew on Flight API)', icon: Users, color: '#f59e0b', group: 'Crews', online: true },
  { id: 'crew_assignment_jcte', name: 'JCTE Crew on Flight', description: 'Crew assignments from the JCTE SOAP source', icon: Users, color: '#f59e0b', group: 'Crews', online: true },
  { id: 'crew_assignment_aims', name: 'AIMS Crew on Flight', description: 'Crew assignments from the AIMS SOAP source', icon: Users, color: '#f59e0b', group: 'Crews', online: true },
  { id: 'crew_assignment_ha', name: 'HA Crew on Flight', description: 'Crew assignments from the HA (Hawaiian) source', icon: Users, color: '#f59e0b', group: 'Crews', online: true },
  { id: 'crew_roster', name: 'Roster Management', description: 'Manages crew schedules and rest', icon: CalendarClock, color: '#f59e0b', group: 'Crews', online: false },
  { id: 'booking', name: 'Ticket Reservation', description: 'Creates PNR bookings', icon: Ticket, color: '#00AF66', group: 'Reservations', online: true },
  { id: 'dh_pnr_reservation', name: 'DH PNR Reservation', description: 'Deadhead crew positioning', icon: Anchor, color: '#00AF66', group: 'Reservations', online: true },
  { id: 'upgrade_agent', name: 'Upgrades', description: 'Seat upgrades and class changes', icon: CreditCard, color: '#00AF66', group: 'Reservations', online: true },
  { id: 'ancillary_agent', name: 'Ancillary Services', description: 'Bags, seats, meals, add-ons', icon: Luggage, color: '#00AF66', group: 'Reservations', online: false },
  { id: 'loyalty_mvp', name: 'Loyalty & MVP', description: 'Mileage plans, tier lookup & benefits', icon: Shield, color: '#8b5cf6', group: 'Loyalty', online: true },
  { id: 'tier_lookup', name: 'Tier Lookup', description: 'Finds matching MVP/oneworld tier', icon: Zap, color: '#8b5cf6', group: 'Loyalty', online: true },
  { id: 'cargo_management', name: 'Cargo Booking', description: 'Creates cargo shipments', icon: Package, color: '#E31837', group: 'CargoOps', online: true },
  { id: 'cargo_tracking', name: 'Cargo Tracking', description: 'Tracks shipment status', icon: Truck, color: '#E31837', group: 'CargoOps', online: true },
  { id: 'cargo_capacity', name: 'Capacity Planning', description: 'Load and weight distribution', icon: BarChart3, color: '#E31837', group: 'CargoOps', online: false },
  { id: 'hazmat_agent', name: 'Hazmat Compliance', description: 'Dangerous goods validation', icon: Shield, color: '#E31837', group: 'CargoOps', online: false },
];

const AGENT_GROUPS = [
  { id: 'FlightOps', name: 'Flight Operations', color: '#0097A9' },
  { id: 'Crews', name: 'Crew Management', color: '#f59e0b' },
  { id: 'Reservations', name: 'Reservations', color: '#00AF66' },
  { id: 'Loyalty', name: 'Loyalty & MVP', color: '#8b5cf6' },
  { id: 'CargoOps', name: 'Cargo Operations', color: '#E31837' },
];

/* ─── Example Prompts (fallback) ─── */
const FALLBACK_PROMPTS = [
  { text: 'Delay a flight and book a PNR for a gold member with wheelchair', icon: Clock },
  { text: 'Book 3 passengers on SEA to SFO with 2 bags each', icon: Ticket },
  { text: 'Create a roundtrip PNR from SEA to LAX, MVP 100K', icon: Plane },
  { text: 'Delay AS flight DCA to SEA by 45 min and book a seat', icon: Sparkles },
];

const SUGGESTION_ICONS = [Clock, Ticket, Plane, Sparkles];

/* ─── Prompt Input ─── */
function PromptInputBar({
  value,
  onChange,
  onSubmit,
  isProcessing,
  inputRef,
  maxWidth = 600,
}) {
  const canSubmit = Boolean(value.trim()) && !isProcessing;

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    input.style.height = 'auto';
    input.style.height = `${Math.max(24, Math.min(input.scrollHeight, 120))}px`;
  }, [inputRef, value]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      role="search"
      aria-label="Test data prompt"
      aria-busy={isProcessing}
      sx={{
        width: '100%',
        maxWidth,
        bgcolor: '#f1f5f9',
        borderRadius: '24px',
        display: 'flex',
        alignItems: 'center',
        minHeight: 52,
        px: 2,
        transition: 'background 0.2s, box-shadow 0.2s, border-color 0.2s',
        '&:focus-within': {
          bgcolor: '#ffffff',
          boxShadow: '0 0 0 2px #6366F1, 0 2px 12px rgba(99,102,241,0.15)',
          borderColor: '#6366F1',
        },
        border: '2px solid #d1d5db',
      }}
    >
      <Sparkles size={16} aria-hidden="true" style={{ color: '#6366F1', flexShrink: 0, marginRight: 10, opacity: 0.7 }} />
      <textarea
        ref={inputRef}
        rows={1}
        className="aitdg-prompt-input"
        aria-label="Describe what test data you need"
        aria-describedby="aitdg-prompt-help"
        placeholder={isProcessing ? 'Agents working...' : 'Describe what test data you need...'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isProcessing}
        autoComplete="off"
        spellCheck="true"
      />
      <span id="aitdg-prompt-help" className="aitdg-sr-only">
        Press Enter to send. Press Shift and Enter for a new line.
      </span>
      <IconButton
        type="submit"
        aria-label={isProcessing ? 'Generating test data' : 'Send prompt'}
        disabled={!canSubmit}
        size="small"
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          ml: 0.5,
          bgcolor: canSubmit ? '#6366F1' : 'transparent',
          color: canSubmit ? '#fff' : '#9ca3af',
          borderRadius: '50%',
          transition: 'all 0.15s',
          '&:hover': { bgcolor: canSubmit ? '#4f46e5' : '#f1f5f9' },
          '&.Mui-disabled': { color: '#d1d5db' },
        }}
      >
        {isProcessing ? <Loader2 size={15} className="aitdg-spin" /> : <Send size={15} />}
      </IconButton>
    </Box>
  );
}

/* ─── Agent Sidebar (shared between landing and chat) ─── */
function AgentSidebar({ activeAgent, delegationAnim, delegationHistory, expandedGroups, setExpandedGroups, highlightAgent }) {
  const onlineCount = AGENT_CONFIG.filter(a => a.online).length;
  const offlineCount = AGENT_CONFIG.filter(a => !a.online).length;

  return (
    <Paper className="aitdg-agent-sidebar" elevation={0}>
      {/* Header */}
      <Box className="aitdg-sidebar-header">
        <span className="aitdg-sidebar-title">Agents</span>
        <Box className="aitdg-sidebar-status">
          <Box className="aitdg-status-dot" />
          <span className="aitdg-status-text">{onlineCount} online · {offlineCount} off</span>
        </Box>
      </Box>

      {/* Agent List */}
      <Box className="aitdg-agent-list">
        {/* Orchestrator */}
        <Box className={`aitdg-orchestrator-card ${activeAgent === 'orchestrator' ? 'active' : ''}`}>
          <Box className="aitdg-orchestrator-icon">
            <Brain size={16} color="#fff" className={activeAgent === 'orchestrator' ? 'aitdg-pulse' : ''} />
          </Box>
          <Box className="aitdg-orchestrator-info">
            <Box className="aitdg-orchestrator-name">
              Orchestrator
              <span className="aitdg-orchestrator-badge">MAIN</span>
            </Box>
            <Box className="aitdg-orchestrator-desc">Coordinates all agent tasks</Box>
          </Box>
          {activeAgent === 'orchestrator' ? (
            <Loader2 size={14} className="aitdg-spin" color="#6366F1" />
          ) : (
            <Box className="aitdg-online-indicator"><Box className="aitdg-online-dot" /><span>online</span></Box>
          )}
        </Box>

        {/* Agent Groups */}
        {AGENT_GROUPS.map((group) => {
          const groupAgents = AGENT_CONFIG.filter(a => a.group === group.id);
          const hasActiveAgent = groupAgents.some(a => a.id === activeAgent || a.id === highlightAgent);
          const isExpanded = hasActiveAgent || expandedGroups[group.id];
          const onlineInGroup = groupAgents.filter(a => a.online).length;

          return (
            <Box key={group.id} className="aitdg-group">
              <Box
                component="button"
                type="button"
                className={`aitdg-group-header ${hasActiveAgent ? 'active' : ''}`}
                onClick={() => setExpandedGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                aria-expanded={Boolean(isExpanded)}
                aria-controls={`aitdg-group-${group.id}`}
              >
                <Box className="aitdg-group-dot" sx={{ backgroundColor: group.color, opacity: hasActiveAgent ? 1 : 0.5 }} />
                <span className="aitdg-group-name">{group.name}</span>
                <span className="aitdg-group-count">{onlineInGroup}/{groupAgents.length}</span>
                <ChevronRight size={12} className={`aitdg-group-chevron ${isExpanded ? 'expanded' : ''}`} />
              </Box>

              {isExpanded && (
                <Box id={`aitdg-group-${group.id}`} className="aitdg-subagents-list">
                  {groupAgents.map((agent) => {
                    const Icon = agent.icon;
                    const isActive = activeAgent === agent.id;
                    const isHighlighted = highlightAgent === agent.id;
                    const isOffline = !agent.online;

                    return (
                      <Box
                        key={agent.id}
                        className={`aitdg-subagent-item ${isActive ? 'active' : ''} ${isHighlighted ? 'highlighted' : ''} ${isOffline ? 'offline' : ''}`}
                        sx={{ color: (isActive || isHighlighted) ? agent.color : undefined }}
                      >
                        <Box className="aitdg-subagent-icon">
                          <Icon size={12} color={(isActive || isHighlighted) ? agent.color : isOffline ? '#b0b8c4' : '#64748b'} className={(isActive || isHighlighted) ? 'aitdg-pulse' : ''} />
                        </Box>
                        <span className="aitdg-subagent-name">{agent.name}</span>
                        {isActive ? (
                          <Loader2 size={11} className="aitdg-spin" color={agent.color} />
                        ) : isHighlighted ? (
                          <Box className="aitdg-highlight-badge" sx={{ color: agent.color }}>searching</Box>
                        ) : isOffline ? (
                          <Box className="aitdg-offline-indicator"><Box className="aitdg-offline-dot" /><span>off</span></Box>
                        ) : (
                          <Box className="aitdg-online-indicator"><Box className="aitdg-online-dot" /><span>ready</span></Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Delegation Animation */}
      {delegationAnim && <DelegationAnimation from={delegationAnim.from} to={delegationAnim.to} />}

      {/* Execution Log */}
      {delegationHistory.length > 0 && !delegationAnim && (
        <Box className="aitdg-exec-log">
          <Box className="aitdg-exec-log-title">Execution Log</Box>
          <Box className="aitdg-history">
            {delegationHistory.map((item, i) => {
              const ag = AGENT_CONFIG.find((a) => a.id === item.agent);
              if (!ag) return null;
              return (
                <Box key={i} className="aitdg-history-item">
                  <Box className="aitdg-history-dot" sx={{ backgroundColor: ag.color }} />
                  <Box className="aitdg-history-content">
                    <Box className="aitdg-history-agent" sx={{ color: ag.color }}>{ag.name}</Box>
                    <Box className="aitdg-history-label">{item.label}</Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

/* ─── Main Component ─── */
export default function AITestDataGeneration() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeAgent, setActiveAgent] = useState(null);
  const [highlightAgent, setHighlightAgent] = useState(null);
  const [delegationAnim, setDelegationAnim] = useState(null);
  const [delegationHistory, setDelegationHistory] = useState([]);
  const [hasStarted, setHasStarted] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [activeView, setActiveView] = useState('chat'); // 'chat' | 'bookings'
  const [suggestions, setSuggestions] = useState(FALLBACK_PROMPTS);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch dynamic suggestions on mount
  useEffect(() => {
    let cancelled = false;
    aiTestDataApi.getSuggestions()
      .then((res) => {
        if (cancelled) return;
        const data = res.data || res;
        if (data.suggestions && Array.isArray(data.suggestions)) {
          setSuggestions(
            data.suggestions.map((text, i) => ({
              text,
              icon: SUGGESTION_ICONS[i % SUGGESTION_ICONS.length],
            }))
          );
        }
      })
      .catch(() => {
        // Keep fallback prompts on error
      })
      .finally(() => {
        if (!cancelled) setSuggestionsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeAgent) {
      const agent = AGENT_CONFIG.find(a => a.id === activeAgent);
      if (agent?.group) {
        // Only expand this agent's group, collapse all others
        setExpandedGroups({ [agent.group]: true });
      }
    }
  }, [activeAgent]);

  useEffect(() => {
    if (highlightAgent) {
      const agent = AGENT_CONFIG.find(a => a.id === highlightAgent);
      if (agent?.group) {
        setExpandedGroups({ [agent.group]: true });
      }
    }
  }, [highlightAgent]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (hasStarted && !isProcessing) {
      inputRef.current?.focus();
    }
  }, [hasStarted, isProcessing]);

  const addMessage = useCallback((role, content, meta = {}) => {
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role, content, meta, timestamp: new Date() },
    ]);
  }, []);

  const showDelegation = async (fromAgent, toAgent) => {
    setDelegationAnim({ from: fromAgent, to: toAgent });
    if (DELEGATION_DELAY) {
      await new Promise((resolve) => setTimeout(resolve, DELEGATION_DELAY));
    }
    setDelegationAnim(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const prompt = input.trim();
    if (!prompt || isProcessing) return;

    if (!hasStarted) setHasStarted(true);
    setInput('');
    addMessage('user', prompt);
    setIsProcessing(true);
    setActiveAgent('orchestrator');
    setDelegationHistory([]);

    try {
      addMessage('agent', 'Thinking...', { agent: 'orchestrator', type: 'thinking' });

      // Build conversation history from prior messages (user + agent complete)
      const history = messages
        .filter(m => m.role === 'user' || (m.role === 'agent' && m.meta?.type === 'complete'))
        .map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content }));

      const response = await aiTestDataApi.runAgentStream(prompt, history);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let thinkingRemoved = false;
      let prevAgent = 'orchestrator';
      const delegations = [{ agent: 'orchestrator', label: 'Started orchestration' }];
      let context = {};
      const pendingMsgIds = [];
      let isFirstThinking = true;

      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') continue;

          let ev;
          try { ev = JSON.parse(payload); } catch { continue; }

          if (!thinkingRemoved) {
            setMessages((prev) => prev.filter(m => m.meta?.type !== 'thinking'));
            thinkingRemoved = true;
          }

          if (ev.type === 'thinking') {
            const msgType = isFirstThinking ? 'plan' : 'response';
            // Show orchestrator as active when it's providing reasoning
            if (!isFirstThinking) {
              setActiveAgent('orchestrator');
            }
            addMessage('agent', ev.content, { agent: 'orchestrator', type: msgType });
            isFirstThinking = false;

          } else if (ev.type === 'tool_call') {
            const agentId = ev.agent || 'orchestrator';

            // Add delay between tool calls for visual pacing (3-4s)
            if (PACING_DELAY) await new Promise(r => setTimeout(r, PACING_DELAY + getVisualJitter()));

            // If booking with MVP/loyalty, show loyalty lookup in chat + highlight sidebar
            const hasMvp = ev.args?.mvp_tiers && ev.args.mvp_tiers !== '[]';
            if (ev.tool === 'book_pnr' && hasMvp) {
              // Parse the MVP tier name for display
              let mvpLabel = 'MVP member';
              try {
                const mvpArr = JSON.parse(ev.args.mvp_tiers);
                if (mvpArr.length > 0) mvpLabel = mvpArr[0].MP_CODE?.replace(/_/g, ' ') || 'MVP member';
              } catch {
                mvpLabel = 'MVP member';
              }

              setHighlightAgent('loyalty_mvp');
              setExpandedGroups({ Loyalty: true });
              delegations.push({ agent: 'loyalty_mvp', label: `Searching ${mvpLabel}...` });
              setDelegationHistory([...delegations]);

              // Show "searching" message in chat
              const loyaltyMsgId = crypto.randomUUID();
              setMessages((prev) => [...prev, {
                id: loyaltyMsgId,
                role: 'agent',
                content: `Searching for ${mvpLabel} profile...`,
                meta: { agent: 'loyalty_mvp', type: 'executing' },
                timestamp: new Date(),
              }]);

              if (PACING_DELAY) {
                await new Promise(resolve => setTimeout(resolve, PACING_DELAY));
              }

              // Update to success
              setMessages((prev) => prev.map(m =>
                m.id === loyaltyMsgId
                  ? { ...m, content: `${mvpLabel} profile fetched successfully`, meta: { ...m.meta, type: 'success' } }
                  : m
              ));
              setHighlightAgent(null);
            }

            if (agentId !== prevAgent) {
              await showDelegation(prevAgent, agentId);
            }
            setActiveAgent(agentId);
            delegations.push({ agent: agentId, label: ev.description });
            setDelegationHistory([...delegations]);

            const msgId = crypto.randomUUID();
            pendingMsgIds.push(msgId);
            setMessages((prev) => [...prev, {
              id: msgId,
              role: 'agent',
              content: ev.description,
              meta: { agent: agentId, type: 'executing' },
              timestamp: new Date(),
            }]);
            prevAgent = agentId;

          } else if (ev.type === 'tool_result') {
            const msgId = pendingMsgIds.shift();
            if (msgId) {
              setMessages((prev) => prev.map(m =>
                m.id === msgId
                  ? { ...m, meta: { ...m.meta, type: ev.success ? 'success' : 'error' } }
                  : m
              ));
            }
            if (ev.success && ev.result) {
              context = { ...context, ...ev.result };
            }

          } else if (ev.type === 'complete') {
            if (prevAgent !== 'orchestrator') {
              await showDelegation(prevAgent, 'orchestrator');
            }
            setActiveAgent('orchestrator');
            setExpandedGroups({});

            let summaryText = ev.content || '';
            const extras = [];
            if (context.flight_number) extras.push(`✈️ Flight: AS${context.flight_number} ${context.departure_airport || ''}→${context.arrival_airport || ''}`);
            if (context.flight_date || context.date) extras.push(`📅 Date: ${context.flight_date || context.date}`);
            if (context.delay_minutes) extras.push(`⏱️ Delayed by: ${context.delay_minutes} min (${context.previous_etd || ''} → ${context.new_etd || ''})`);
            if (context.PNR) extras.push(`🎫 PNR: ${context.PNR}`);
            if (context.passengers && Array.isArray(context.passengers)) {
              context.passengers.forEach(p => {
                let line = `👤 ${(p.name || '').trim()}`;
                if (p.seat) line += ` | Seat ${p.seat}`;
                if (p.ticket) line += ` | Ticket ${p.ticket}`;
                extras.push(line);
              });
            }
            if (context.ssr_applied?.length) extras.push(`♿ SSR: ${context.ssr_applied.map(s => s.SSR_Code).join(', ')}`);
            if (context.mvp_applied?.length) extras.push(`⭐ MVP: ${context.mvp_applied.map(m => m.MP_CODE).join(', ')}`);
            if (context.bags_applied?.length) extras.push(`🧳 Bags: ${context.bags_applied.map(b => `${b.Bag_Count} for pax ${b.Bags_Pax_Num}`).join(', ')}`);
            if (extras.length) summaryText += '\n\n' + extras.join('\n');
            addMessage('agent', summaryText, { agent: 'orchestrator', type: 'complete' });

          } else if (ev.type === 'error') {
            addMessage('agent', `Error: ${ev.content}`, { agent: 'orchestrator', type: 'error' });
          }
        }
      }

    } catch (err) {
      addMessage('agent', `Error: ${err.message || 'Something went wrong. Please try again.'}`, {
        agent: 'orchestrator',
        type: 'error',
      });
    } finally {
      setMessages((prev) => prev.filter(message => message.meta?.type !== 'thinking'));
      setIsProcessing(false);
      setActiveAgent(null);
      setDelegationAnim(null);
    }
  };

  const handleExampleClick = (prompt) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  // ─── My Bookings View ───
  if (activeView === 'bookings') {
    return (
      <Box className="aitdg-container">
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* View Toggle Header */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                startIcon={<Sparkles size={14} />}
                onClick={() => setActiveView('chat')}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                  color: '#64748b', borderRadius: '8px', px: 1.5, py: 0.5,
                  '&:hover': { bgcolor: '#f1f5f9' },
                }}
              >
                Chat
              </Button>
              <Button
                size="small"
                startIcon={<List size={14} />}
                onClick={() => setActiveView('bookings')}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                  color: '#6366F1', bgcolor: '#eef2ff', borderRadius: '8px', px: 1.5, py: 0.5,
                }}
              >
                My Bookings
              </Button>
            </Box>
          </Box>
          {/* Bookings Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <TdmBookings />
          </Box>
        </Box>

        {/* Sidebar always visible */}
        <AgentSidebar
          activeAgent={activeAgent}
          highlightAgent={highlightAgent}
          delegationAnim={delegationAnim}
          delegationHistory={delegationHistory}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
        />
      </Box>
    );
  }

  // ─── Welcome Screen ───
  if (!hasStarted) {
    return (
      <Box className="aitdg-container">
        <Box className="aitdg-landing">
          {/* View Toggle Header */}
          <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 0.5 }}>
            <Button
              size="small"
              startIcon={<Sparkles size={14} />}
              onClick={() => setActiveView('chat')}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                color: '#6366F1', bgcolor: '#eef2ff', borderRadius: '8px', px: 1.5, py: 0.5,
              }}
            >
              Chat
            </Button>
            <Button
              size="small"
              startIcon={<List size={14} />}
              onClick={() => setActiveView('bookings')}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                color: '#64748b', borderRadius: '8px', px: 1.5, py: 0.5,
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              My Bookings
            </Button>
          </Box>

          <Box className="aitdg-landing-content">
            <Box className="aitdg-landing-icon">
              <Sparkles size={28} color="#6366F1" />
            </Box>
            <Typography component="h1" sx={{ fontWeight: 700, color: '#01426A', mt: 2.5, mb: 0.5, fontSize: '1.5rem', letterSpacing: '-0.02em' }}>
              Agentic Test Data Orchestrator
            </Typography>
            <Typography sx={{ color: '#6b7280', mb: 4, fontSize: '0.875rem', textAlign: 'center', maxWidth: 420, lineHeight: 1.6 }}>
              Generate test data through natural language — flights, PNR bookings, delays, and more.
            </Typography>

            {/* Dynamic prompt suggestions */}
            <Box className="aitdg-landing-examples">
              {suggestionsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <Loader2 size={14} className="aitdg-spin" color="#6366F1" />
                  <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>Loading suggestions...</Typography>
                </Box>
              ) : (
                suggestions.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <Box
                      key={i}
                      component="button"
                      type="button"
                      className="aitdg-landing-example-item"
                      onClick={() => handleExampleClick(item.text)}
                    >
                      <Icon size={13} color="#6366F1" className="example-icon" />
                      <Typography sx={{ fontSize: '12px', color: '#4b5563', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                        {item.text}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>

            <PromptInputBar
              value={input}
              onChange={setInput}
              onSubmit={handleSubmit}
              isProcessing={isProcessing}
              inputRef={inputRef}
            />
          </Box>
        </Box>

        {/* Sidebar always visible */}
        <AgentSidebar
          activeAgent={activeAgent}
          highlightAgent={highlightAgent}
          delegationAnim={delegationAnim}
          delegationHistory={delegationHistory}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
        />
      </Box>
    );
  }

  // ─── Chat Interface ───
  return (
    <Box className="aitdg-container">
      <Box className="aitdg-chat-area">
        {/* View Toggle Header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
              size="small"
              startIcon={<Sparkles size={14} />}
              onClick={() => setActiveView('chat')}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                color: '#6366F1', bgcolor: '#eef2ff', borderRadius: '8px', px: 1.5, py: 0.5,
              }}
            >
              Chat
            </Button>
            <Button
              size="small"
              startIcon={<List size={14} />}
              onClick={() => setActiveView('bookings')}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                color: '#64748b', borderRadius: '8px', px: 1.5, py: 0.5,
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              My Bookings
            </Button>
          </Box>
        </Box>

        {/* Messages */}
        <Box className="aitdg-messages">
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}
          {isProcessing && !messages.length && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LinearProgress sx={{ width: 200, borderRadius: 2 }} />
            </Box>
          )}
          <div ref={chatEndRef} />
        </Box>

        {/* Input */}
        <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center' }}>
          <PromptInputBar
            value={input}
            onChange={setInput}
            onSubmit={handleSubmit}
            isProcessing={isProcessing}
            inputRef={inputRef}
            maxWidth={680}
          />
        </Box>
      </Box>

      {/* Sidebar always visible */}
      <AgentSidebar
        activeAgent={activeAgent}
        highlightAgent={highlightAgent}
        delegationAnim={delegationAnim}
        delegationHistory={delegationHistory}
        expandedGroups={expandedGroups}
        setExpandedGroups={setExpandedGroups}
      />
    </Box>
  );
}

/* ─── Delegation Animation Component ─── */
function DelegationAnimation({ from, to }) {
  const fromAgent = AGENT_CONFIG.find((a) => a.id === from);
  const toAgent = AGENT_CONFIG.find((a) => a.id === to);
  if (!fromAgent || !toAgent) return null;

  const FromIcon = fromAgent.icon;
  const ToIcon = toAgent.icon;

  return (
    <Fade in>
      <Box className="aitdg-delegation">
        <Box className="aitdg-delegation-title">
          {from === 'orchestrator' ? 'Delegating' : 'Handing Over'}
        </Box>
        <Box className="aitdg-delegation-flow">
          <Box className="aitdg-delegation-agent">
            <Avatar sx={{ width: 30, height: 30, background: from === 'orchestrator' ? 'linear-gradient(135deg, #6366F1, #818cf8)' : `${fromAgent.color}12`, border: `1.5px solid ${fromAgent.color}40` }}>
              <FromIcon size={14} color={from === 'orchestrator' ? '#fff' : fromAgent.color} />
            </Avatar>
            <span className="aitdg-delegation-agent-name">{fromAgent.name}</span>
          </Box>

          <Box className="aitdg-delegation-connector">
            <Box className="aitdg-delegation-particle p1" sx={{ backgroundColor: '#6366F1' }} />
            <Box className="aitdg-delegation-particle p2" sx={{ backgroundColor: '#8b5cf6' }} />
            <Box className="aitdg-delegation-particle p3" sx={{ backgroundColor: toAgent.color }} />
            <Zap size={10} className="aitdg-delegation-bolt" color="#6366F1" />
          </Box>

          <Box className="aitdg-delegation-agent">
            <Avatar sx={{ width: 30, height: 30, backgroundColor: `${toAgent.color}12`, border: `1.5px solid ${toAgent.color}`, animation: 'pulseRing 1s ease-in-out infinite' }}>
              <ToIcon size={14} color={toAgent.color} />
            </Avatar>
            <span className="aitdg-delegation-agent-name">{toAgent.name}</span>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
}

/* ─── Message Bubble ─── */
function MessageBubble({ message }) {
  const { role, content, meta, timestamp } = message;
  const isUser = role === 'user';
  const agent = meta?.agent ? AGENT_CONFIG.find((a) => a.id === meta.agent) : null;

  if (isUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
        <Box sx={{ maxWidth: '70%', bgcolor: '#01426A', color: '#fff', px: 2.5, py: 1.5, borderRadius: '18px 18px 4px 18px' }}>
          <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}>{content}</Typography>
          <Typography sx={{ opacity: 0.5, mt: 0.5, fontSize: '0.68rem', textAlign: 'right' }}>
            {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>
      </Box>
    );
  }

  const agentColor = agent?.color || '#6366F1';
  const AgentIcon = agent?.icon || Bot;
  const type = meta?.type || '';

  const bgMap = {
    thinking: '#fffbeb',
    executing: '#f8fafc',
    success: '#f0fdf4',
    error: '#fef2f2',
    complete: '#f8fafc',
    plan: '#f8f9ff',
    response: '#f8fafc',
  };
  const borderMap = {
    thinking: '#fde68a',
    executing: '#e2e8f0',
    success: '#bbf7d0',
    error: '#fecaca',
    complete: '#e2e8f0',
    plan: '#c7d2fe',
    response: '#e2e8f0',
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: `${agentColor}10`,
          border: `1.5px solid ${agentColor}30`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mt: 0.3,
        }}
      >
        <AgentIcon size={12} color={agentColor} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {agent && (
          <Typography sx={{ fontWeight: 600, color: agentColor, fontSize: '0.75rem', mb: 0.3, display: 'block' }}>
            {agent.name}
          </Typography>
        )}
        <Box
          sx={{
            bgcolor: bgMap[type] || '#f8fafc',
            border: `1px solid ${borderMap[type] || '#e2e8f0'}`,
            borderRadius: '4px 14px 14px 14px',
            px: 2,
            py: 1.2,
            maxWidth: '90%',
          }}
        >
          <MessageContent content={content} type={type} />
          {type === 'executing' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.8 }}>
              <Box className="aitdg-dots-wave">
                <Box className="aitdg-dot" />
                <Box className="aitdg-dot" />
                <Box className="aitdg-dot" />
              </Box>
              <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: 500, fontStyle: 'italic' }}>
                executing
              </Typography>
            </Box>
          )}
          {type === 'success' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.8 }}>
              <CheckCircle2 size={11} color="#16a34a" />
              <Typography sx={{ fontSize: '0.65rem', color: '#16a34a', fontWeight: 500 }}>
                completed
              </Typography>
            </Box>
          )}
          {type === 'error' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.8 }}>
              <Typography sx={{ fontSize: '0.65rem', color: '#dc2626', fontWeight: 500 }}>
                failed
              </Typography>
            </Box>
          )}
        </Box>
        <Typography sx={{ color: '#9ca3af', fontSize: '0.65rem', mt: 0.3 }}>
          {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>
    </Box>
  );
}

/* ─── Message content renderer ─── */
function MessageContent({ content, type }) {
  return (
    <Box className={`aitdg-markdown-content ${type === 'plan' ? 'aitdg-plan-content' : ''}`}>
      <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
    </Box>
  );
}
